import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { faEnvelope, faPhone, faLink } from "@fortawesome/pro-regular-svg-icons";
import { TeamListElement, TeamListMinimum, TeamService, Team } from "../../../../client/api";
import { callClientFunc } from "../../../../client";
import { ClavaContext } from "../../../../config/contexts";
import { errorLogging } from "../../../../config/utils";
import { translate } from "../../../../config/translator";
import RoundedRectangle from "../../../components/RoundedRectangle";
import ClavaTextInput from "../../../components/ClavaTextInput";
import loadingGif from "../../../../assets/images/loading.gif";
import SuccessesManagement from "../SuccessesManagement";

const ContactsManagement: React.FC<{ team: TeamListMinimum | TeamListElement }> = ({ team }) => {
  const { l } = useContext(ClavaContext);
  const [teamData, setTeamData] = useState<Team | undefined>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  
  const [president, setPresident] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");

  const presidentInvalid = useRef(false);
  const emailInvalid = useRef(false);
  const phoneInvalid = useRef(false);
  const instagramInvalid = useRef(false);
  const facebookInvalid = useRef(false);
  const errorStateActive = useRef(false);

  const isValidEmail = useCallback((emailString: string): boolean => {
    if (!emailString.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailString);
  }, []);

  const isValidUrl = useCallback((urlString: string): boolean => {
    if (!urlString.trim()) return true;
    try {
      const parsedUrl = new URL(urlString);
      return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    callClientFunc(
      TeamService.getTeamByIdTeamTeamIdGet,
      false,
      team.id
    ).then((fullTeam) => {
      setTeamData(fullTeam);
      setPresident(fullTeam.president || "");
      setEmail(fullTeam.mail || "");
      setPhone(fullTeam.phone || "");
      setInstagram(fullTeam.instagram || "");
      setFacebook(fullTeam.facebook || "");
      setLoading(false);
    }, (error) => {
      errorLogging(error);
      setLoading(false);
    });
  }, [team.id]);

  const validateForm = useCallback((): boolean => {
    const presidentValid = president.trim().length >= 2;
    const emailValid = isValidEmail(email);
    const phoneValid = phone.trim().length === 0 || phone.trim().length >= 3;
    const instagramValid = instagram.trim().length === 0 || isValidUrl(instagram);
    const facebookValid = facebook.trim().length === 0 || isValidUrl(facebook);

    presidentInvalid.current = !presidentValid;
    emailInvalid.current = !emailValid;
    phoneInvalid.current = !phoneValid;
    instagramInvalid.current = !instagramValid;
    facebookInvalid.current = !facebookValid;

    return presidentValid && emailValid && phoneValid && instagramValid && facebookValid;
  }, [president, email, phone, instagram, facebook, isValidEmail, isValidUrl]);

  const handleSave = useCallback(() => {
    errorStateActive.current = true;
    if (!validateForm() || saving || !teamData) {
      return;
    }

    setSaving(true);
    const patchData: any = {};
    
    if (president !== teamData.president) {
      patchData.president = president || null;
    }
    if (email !== teamData.mail) {
      patchData.mail = email || null;
    }
    if (phone !== teamData.phone) {
      patchData.phone = phone || null;
    }
    if (instagram !== teamData.instagram) {
      patchData.instagram = instagram || null;
    }
    if (facebook !== teamData.facebook) {
      patchData.facebook = facebook || null;
    }

    if (Object.keys(patchData).length === 0) {
      setSaving(false);
      setEditing(false);
      return;
    }

    callClientFunc(
      TeamService.patchTeamTeamTeamIdPatch,
      false,
      team.id,
      patchData
    ).then((updatedTeam) => {
      setTeamData(updatedTeam);
      setEditing(false);
      setSaving(false);
    }, (error) => {
      errorLogging(error);
      setSaving(false);
    });
  }, [president, email, phone, instagram, facebook, teamData, team.id, validateForm, saving]);

  const handleCancel = useCallback(() => {
    if (teamData) {
      setPresident(teamData.president || "");
      setEmail(teamData.mail || "");
      setPhone(teamData.phone || "");
      setInstagram(teamData.instagram || "");
      setFacebook(teamData.facebook || "");
    }
    errorStateActive.current = false;
    setEditing(false);
  }, [teamData]);

  if (loading) {
    return (
      <div className="text-center py-8 text-font-600 dark:text-font-600-dark">
        {translate("loading", l)}
      </div>
    );
  }

  return (
    <>
    <RoundedRectangle
      className="w-full bg-bg-secondary dark:bg-bg-secondary-dark relative"
      hasBorder={false}
      hasTitle
      titleKey="contacts"
    >
      {!editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="absolute top-4 right-4 text-primary hover:text-btn-active transition-colors text-sm font-medium"
        >
          {translate("edit", l)}
        </button>
      )}
      <div className="px-6 py-4">
        <div className="flex flex-col gap-4">

          <div className="flex flex-row gap-4">
            <div className="flex-1">
              <div className="mb-1">
                <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
                  {translate("president", l)}
                </span>
              </div>
              <ClavaTextInput
                value={president}
                onChange={setPresident}
                disabled={!editing}
                error={presidentInvalid.current && errorStateActive.current}
                placeholder={translate("president", l)}
              />
            </div>
            <div className="flex-1">
              <div className="mb-1">
                <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
                  {translate("email", l)}
                </span>
              </div>
              <ClavaTextInput
                value={email}
                onChange={setEmail}
                disabled={!editing}
                error={emailInvalid.current && errorStateActive.current}
                placeholder={translate("email", l)}
                type="email"
                iconL={faEnvelope}
              />
            </div>
          </div>

          <div className="flex flex-row gap-4">
            <div className="flex-1">
              <div className="mb-1">
                <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
                  {translate("phone", l)}
                </span>
              </div>
              <ClavaTextInput
                value={phone}
                onChange={setPhone}
                disabled={!editing}
                error={phoneInvalid.current && errorStateActive.current}
                placeholder={translate("phone", l)}
                type="text"
                iconL={faPhone}
              />
            </div>
            <div className="flex-1">
              <div className="mb-1">
                <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
                  {translate("instagram", l)}
                </span>
              </div>
              <ClavaTextInput
                value={instagram}
                onChange={setInstagram}
                disabled={!editing}
                error={instagramInvalid.current && errorStateActive.current}
                placeholder="https://instagram.com/..."
                iconL={faLink}
              />
            </div>
          </div>

          <div>
            <div className="mb-1">
              <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
                {translate("facebook", l)}
              </span>
            </div>
            <ClavaTextInput
              value={facebook}
              onChange={setFacebook}
              disabled={!editing}
              error={facebookInvalid.current && errorStateActive.current}
              placeholder="https://facebook.com/..."
              iconL={faLink}
            />
          </div>

          {editing && (
            <div className="flex flex-col gap-2 mt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="w-full rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-red active:bg-red hover:bg-red text-white text-sm font-semibold disabled:opacity-60"
              >
                {translate("cancel", l)}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-primary active:bg-btn-active hover:bg-btn-active text-font-dark text-sm font-semibold disabled:opacity-60"
              >
                {saving ? (
                  <img src={loadingGif} alt="Loading" className="w-4 h-4" />
                ) : (
                  translate("save", l)
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </RoundedRectangle>
    <SuccessesManagement team={team} />
    </>
  );
};

export default ContactsManagement;

